import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import AlertDialog from "../AlertDialog";
import { useDispatch, useSelector } from "react-redux";
import InputText from "../UI/Inputs/InputText";
import { setAppLoading, setChangePassword } from "../../store/reducers/app";
import { changePassword, checkCurrentPassword } from "../../store/actions/user";
import { adviceAlert, errorRetryAlert, waitingAndSuccessAlert } from "../../utils/alerts";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import { urlFormLogout } from "../../api/urls";
import { getGlobalCsrToken } from "../../api";

const FormChangePassword = ({ onClose, origItem, onDelete, onSave }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const [currentPassword, setCurrentPassword] = useState(null);
  const [newPassword, setNewPassword] = useState(null);
  const [repeatNewPassword, setRepeatNewPassword] = useState(null);
  const logoutRef = useRef(null);

  const saveHandler = () => {
    //comprobamos contraseña actual
    dispatch(setAppLoading(true));
    checkCurrentPassword(currentPassword)
      .then((r) => {
        if (r) {
          //mandamos endpoint de cambio de contraseña
          waitingAndSuccessAlert({
            t: t,
            title: t("saving"),
            waitingFunction: () => changePassword(currentPassword, newPassword),
            successMessage: t("changePassword.passwordChanged"),
            ignoreResult: true,
            onSuccess: () => {
              //refrescamos página tras cambiar la contraseña
              // window.location.reload();
              logoutRef.current.submit();
            },
            errorTime: 5000,
          });
        } else {
          adviceAlert({ t: t, text: t("changePassword.currentError") });
        }
      })
      .catch((e) => {
        console.error("Error get rol actions: ", e);
        errorRetryAlert({ t: t, onRetry: saveHandler, error: e });
      })
      .finally(() => {
        dispatch(setAppLoading(false));
      });
  };
  const closeHandler = () => {
    dispatch(setChangePassword(false));
  };

  const noMatchPassword = useMemo(() => {
    return newPassword && repeatNewPassword && newPassword !== repeatNewPassword;
  }, [newPassword, repeatNewPassword]);

  //Mínimo 12 caracteres
  const rule1Ok = useMemo(() => {
    return newPassword && newPassword.length >= 12;
  }, [newPassword]);

  //Al menos 4 caracteres alfabéticos
  const rule2Ok = useMemo(() => {
    return newPassword && newPassword.match(/[a-zA-Z]/g) && newPassword.match(/[a-zA-Z]/g).length > 3;
  }, [newPassword]);

  //Al menos una mayúscula
  const rule3Ok = useMemo(() => {
    return newPassword && /[A-Z]/.test(newPassword);
  }, [newPassword]);

  //Al menos una minúscula
  const rule4Ok = useMemo(() => {
    return newPassword && /[a-z]/.test(newPassword);
  }, [newPassword]);

  //al menos 1 caracter especial
  const rule5Ok = useMemo(() => {
    return newPassword && /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(newPassword);
  }, [newPassword]);

  //no contener datos personales
  const rule6Ok = useMemo(() => {
    const personalWords = user.getName().toLowerCase().trim().split(" ");
    return newPassword && !personalWords.find((word) => newPassword.toLowerCase().indexOf(word) !== -1);
  }, [newPassword]);

  const checkCurrentPasswordHandler = () => {
    dispatch(setAppLoading(true));
    checkCurrentPassword(currentPassword)
      .then((r) => {
        if (!r) {
          adviceAlert({ t: t, text: t("changePassword.currentError") });
        }
      })
      .catch((e) => {
        console.error("Error check current password: ", e);
        errorRetryAlert({ t: t, onRetry: checkCurrentPasswordHandler, error: e });
      })
      .finally(() => {
        dispatch(setAppLoading(false));
      });
  };
  return (
    <AlertDialog
      transition
      show
      onClose={!user.getChangePassword() ? closeHandler : null}
      title={t("changePassword.title")}
      acceptTitle={t("actions.confirm")}
      closeTitle={t("actions.cancel")}
      blockClose
      maxWidth="sm"
      contentClassName="pt-1"
      onAccept={saveHandler}
      fullScreenMobile
      acceptDisabled={
        !rule1Ok ||
        !rule2Ok ||
        !rule3Ok ||
        !rule4Ok ||
        !rule5Ok ||
        !rule6Ok ||
        !newPassword ||
        !repeatNewPassword ||
        noMatchPassword
      }
    >
      <div className="d-flex flex-column">
        <InputText
          label={t("changePassword.current")}
          value={currentPassword}
          onChange={setCurrentPassword}
          inputType="password"
          id="id-current-password"
          name="current-password"
          onBlur={checkCurrentPasswordHandler}
        />
        <InputText
          label={t("changePassword.new")}
          value={newPassword}
          onChange={setNewPassword}
          inputType="password"
          id="id-new-password"
          name="new-password"
        />
        <InputText
          label={t("changePassword.repeat")}
          value={repeatNewPassword}
          onChange={setRepeatNewPassword}
          inputType="password"
          error={noMatchPassword}
          id="id-repeat-new-password"
          name="repeat-new-password"
          helperText={noMatchPassword ? t("changePassword.passwordsNoMatch") : null}
        />

        <Typography className="mt-2">{t("changePassword.rules")}</Typography>
        <List dense>
          <ListItemRule checked={rule1Ok} text={t("changePassword.rule1")} />
          <ListItemRule checked={rule2Ok} text={t("changePassword.rule2")} />
          <ListItemRule checked={rule3Ok} text={t("changePassword.rule3")} />
          <ListItemRule checked={rule4Ok} text={t("changePassword.rule4")} />
          <ListItemRule checked={rule5Ok} text={t("changePassword.rule5")} />
          <ListItemRule checked={rule6Ok} text={t("changePassword.rule6")} />

          <Divider className="mt-2 mb-2" />
          <ListItem>
            <ListItemText primary={t("changePassword.extraRule1")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("changePassword.extraRule2")} />
          </ListItem>
        </List>
      </div>
      <form id="logoutForm" action={urlFormLogout} method="post" ref={logoutRef} style={{ display: "none" }}>
        <Button data-tut="user-menu-logout" variant="outlined" size="small" type="submit"></Button>
        <input type="hidden" name="redirect" value="landingpage" />
        <input type="hidden" name="_csrf" value={getGlobalCsrToken()} />
      </form>
    </AlertDialog>
  );
};

export default FormChangePassword;

const ListItemRule = ({ checked, text }) => {
  return (
    <ListItem>
      <ListItemIcon>
        {checked ? <CheckBoxOutlinedIcon color="primary" /> : <CheckBoxOutlineBlankOutlinedIcon />}
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );
};
